'use client';

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getListing } from '@/lib/api';
import { recallPhotos, rememberPhotos } from '@/lib/photoMemory';
import type { Listing, Photo } from '@/types/listing';

const EMPTY: Photo[] = [];

/**
 * The listing endpoint answered, and the answer was "no photos yet" — the photo
 * pipeline runs behind ingestion, so this is a real state, not a failure. Marked
 * so it can be told apart from a network blip: one is worth retrying, the other
 * is just the truth for the next few minutes.
 */
class NoPhotosYet extends Error {
  readonly noPhotos = true;
}
const isNoPhotosYet = (err: unknown): boolean => (err as NoPhotosYet | null)?.noPhotos === true;

export interface ListingPhotos {
  /** Best photo set known for this listing right now. */
  photos: Photo[];
  /**
   * A backfill request is outstanding, so `photos` being empty is "we are still
   * looking", not "there are none". Tied to the request, never to the shape of
   * the data — a settled answer is an answer, even when it is empty, and the
   * surface must stop shimmering and show something real.
   */
  pending: boolean;
}

/**
 * The photos to paint for a listing, from whichever source actually has them.
 *
 * Three payloads hand the UI a listing with `photos: []` next to a `photoCount`
 * in the double digits, and the type cannot tell them apart:
 *
 *  1. the **light** search projection the map uses (500 matches, photos omitted
 *     by design — see `lib/photoMemory`),
 *  2. the full search when **Meilisearch lags** the database on freshly ingested
 *     homes (measured 2026-07-24: 6 of 240 results, *all* of them at the top of
 *     the default "Newest" sort — the cards above the fold),
 *  3. a **client refetch** of a search the server had already repaired: `/homes`
 *     server-renders with `withBackfilledPhotos`, and the first filter change,
 *     page turn or map pan replaces that with the raw payload.
 *
 * So the hook does two things. It remembers every non-empty photo set the
 * session sees, so a later photoless projection of the same home cannot blank a
 * card that is already showing a photo (case 1 and 3, repaired with no network
 * at all). And when nothing is known, it fetches the listing — but only for
 * cards on screen, only when the record demonstrably contradicts itself
 * (`photoCount > 0` with nothing in `photos`), and never for a home that
 * genuinely has no photos.
 *
 * `pending` exists so the caller can shimmer *while the answer is outstanding*
 * and fall back to a real placeholder once it is not. The previous version
 * derived "pending" from the data shape instead, so a card whose backfill came
 * back empty, failed, or never ran sat as an empty tile with no `<img>` at all —
 * a blank beige void, no photo, no count badge — for as long as the page was
 * open. That is the defect this shipped with; it was live on letsgoreal.com.
 */
export function useListingPhotos(listing: Listing, inView: boolean): ListingPhotos {
  const own = listing.photos?.length ? listing.photos : EMPTY;
  const known = own.length > 0 ? own : (recallPhotos(listing.id) ?? EMPTY);
  const indexIsBehind = known.length === 0 && (listing.photoCount ?? 0) > 0;

  const { data, isFetching } = useQuery({
    queryKey: ['listing-photos', listing.id],
    queryFn: () =>
      getListing(listing.id).then((l) => {
        const photos = l.photos ?? EMPTY;
        // An empty answer is not worth caching for fifteen minutes as though it
        // were the truth: the photo pipeline fills these in minutes after
        // ingestion. Rejecting keeps it out of the success cache, so the next
        // mount asks again instead of pinning "no photos" on a home with 23.
        if (photos.length === 0) throw new NoPhotosYet(`listing ${l.id} has no photos yet`);
        return photos;
      }),
    enabled: indexIsBehind && inView,
    // The photo set for a listing barely moves, and the whole point is to stop
    // re-asking once we have it.
    staleTime: 15 * 60 * 1000,
    // One retry for a blip; none for a straight answer — re-asking a listing
    // that told us it has no photos yet is 20 pointless requests per page.
    retry: (failureCount, error) => failureCount < 1 && !isNoPhotosYet(error),
  });

  const photos = known.length > 0 ? known : (data ?? EMPTY);

  // Remember outside render: a photo set seen once must survive the next
  // photoless projection of the same home, whichever surface it arrives on.
  useEffect(() => {
    rememberPhotos(listing.id, photos);
  }, [listing.id, photos]);

  return { photos, pending: photos.length === 0 && isFetching };
}
